import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import {
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import { HighlightOffRounded, InsertDriveFileOutlined, Publish } from '@material-ui/icons';
import PDFIcon from '../icons/PDF-icon';
import {
  ActionTakenType,
  DocumentTemplateType,
  IFile,
  INumberedDocumentView,
} from '../../api-client/autogenerated';
import {
  copyAdditionalReviewFromStaging,
  submitAdditionalReviewByDocumentId,
  submitAdditionalReviewForPackageByDocumentId,
} from '../../models/api/documents';
import CircularLoader from '../loader/CircularLoader';
import {
  downloadFileAsConsultant,
  fileIsPdf,
  getPublicDocumentKey,
  hasSubstitutionRequestForm,
  openInBluebeam,
  openInNewTab,
  waitForFileToBeVerified,
  waitForFileToBeVerifiedByUrl,
} from '../../scripts/utils';
import { BluebeamButton, CancelButton, DownloadButton } from '../custom-components/CustomButtons';
import { useDispatch, useSelector } from 'react-redux';
import { getDocumentState } from '../../features/document/selectors';
import { fetchDocument, reloadDocument } from '../../features/document/actions';
import BluebeamFlattenDialog from './BluebeamFlattenDialog';
import { addSnackbar } from '../../features/snackbar/actions';
import { allowNavigation, blockNavigation } from '../../features/navigation/actions';
import { getNavigationState } from '../../features/navigation/selectors';
import {
  getActionTakenLabelOverrides,
  getDisabledActionTakenTypes,
} from '../../features/action-taken-labels/selectors';
import { getCustomActionTakenLabel, isActionTakenEnabled } from '../../scripts/action-taken-labels';
import {
  flattenBluebeamByDocumentId,
  getLatestSubmissionFileByDocumentId,
} from '../../models/api/files';
import FileUploadDialog from './FileUploadDialog';
import { deleteFile, uploadToStaging } from '../../models/api/filesystem';
import { getUserState } from '../../features/user/selectors';
import { getDocumentsType } from '../../features/documents/selectors';
import { MULTI_PART_FILE_SIZE } from '../../scripts/constants';
import PackageListTable, {
  SubmittalInfo,
} from '../submittal-action-center/packages/PackageListTable';
import BluebeamFileField from '../submittal-action-center/BluebeamFileField';
import { getNextBluebeamVersionName } from '../submittal-action-center/bluebeam-version-name-utils';
import SubstitutionRequestInfoDialog from './SubstitutionRequestInfoDialog';
import {
  ConsultantReviewStatus,
  isConsultantReviewSubmitDisabled,
} from './consultant-review-submit';

interface ConsultantFileUploadDialogProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  reviewDocument: INumberedDocumentView;
  refreshNotifications?: () => Promise<void>;
  packageDocuments?: INumberedDocumentView[] | null;
}

const defaultTipHeading =
  'Markups added in Bluebeam are automatically shared among all reviewers of this file.';
const designPackageTipHeading =
  'Markups added in Bluebeam Revu are automatically flattened shared among all reviewers of this file.';
const defaultTipBody =
  'Downloading the file for offline review will create a new version of the file which is visible to all reviewers after upload. However, this marked-up version may have to be manually combined with other reviews by the architect before return to contractor.';
const designPackageTipBody =
  'Downloading the file for offline review will create a new version of the file which is visible to all reviewers after upload. However, this marked-up version may have to be manually combined with other reviews.';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
    },
    title: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '26px',
      lineHeight: '30px',
      textAlign: 'left',
      textTransform: 'none',
      whiteSpace: 'nowrap',
      color: '#0947B9',
      marginBottom: '8px',
      maxWidth: 800,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    subtitle: {
      width: 'fit-content',
      maxWidth: '550px',
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontSize: '16px',
      lineHeight: '21px',
      textAlign: 'left',
      textTransform: 'none',
      color: '#464546',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    description: {
      width: '90%',
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontSize: '14px',
      lineHeight: '18px',
      textAlign: 'left',
      textTransform: 'none',
      color: '#464546',
      whiteSpace: 'normal',
      marginTop: 8,
    },
    titleContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '-16px',
      marginRight: '4px',
    },
    contentContainer: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    rootIconButton: {
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    textfield: {
      width: '320px',
    },
    actions: {
      padding: '8px 24px 28px',
      flexDirection: 'column',
      alignItems: 'center',
    },
    file: {
      display: 'inline-flex',
      marginTop: '16px',
      justifyContent: 'center',
      alignItems: 'center',
    },
    fileText: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '11px',
      lineHeight: '13px',
      textAlign: 'center',
      textTransform: 'none',
      color: '#949494', // Gray 400
      paddingLeft: 8,
    },
    columnLayout: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    rowLayout: {
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
    },
  }),
);

export default function ConsultantFileUploadDialog(props: ConsultantFileUploadDialogProps) {
  const classes = useStyles();

  const {
    open,
    handleClose,
    title,
    reviewDocument,
    refreshNotifications,
    packageDocuments,
  } = props;

  const docType = useSelector(getDocumentsType);
  const user = useSelector(getUserState);
  const actionTakenLabels = useSelector(getActionTakenLabelOverrides);
  const disabledActionTaken = useSelector(getDisabledActionTakenTypes);
  const parentDocument = useSelector(getDocumentState)!;
  const shouldBlockNavigation = useSelector(getNavigationState);
  const dispatch = useDispatch();

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [contractorInfoDialogOpen, setContractorInfoDialogOpen] = useState(false);
  const [bluebeamMarkupDialogOpen, setBluebeamMarkupDialogOpen] = useState(false);
  const [bluebeamStudioDialogOpen, setBluebeamStudioDialogOpen] = useState(false);
  // The file handed to Bluebeam. Set once markup is launched: the version its markups produce is
  // named under the markup button and posted when this review is submitted.
  const [bluebeamFile, setBluebeamFile] = useState<IFile>();
  const [bluebeamFilename, setBluebeamFilename] = useState('');
  const [bluebeamMarkupReady, setBluebeamMarkupReady] = useState(false);

  const [latestFile, setLatestFile] = useState<IFile>();
  const [isFetchingLatestFile, setIsFetchingLatestFile] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [readyForReview, setReadyForReview] = useState(false);
  // Review complete with the markups left in the Bluebeam Studio session rather than posted as a
  // file here -- only offered when the parent document has a studio session.
  const [markupsInStudio, setMarkupsInStudio] = useState(false);
  const [inputActionTaken, setInputActionTaken] = useState<ActionTakenType>();
  const [inputComment, setInputComment] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [stagingFileKey, setStagingFileKey] = useState<string>();
  const [stagingUploadVerified, setStagingUploadVerified] = useState(false);

  const [submittalInfo, setSubmittalInfo] = useState<SubmittalInfo[]>(
    packageDocuments?.map((doc) => {
      return {
        document: doc,
        actionTaken: undefined,
        comment: '',
      };
    }) || [],
  );

  const isPackage = !!packageDocuments?.length;
  // A Bidder RFI is reviewed exactly like a Construction RFI: the consultant answers the question
  // with comments and/or a marked-up file, and picks no recommended action.
  const isRfi =
    docType === DocumentTemplateType.RequestsForInformation ||
    [docType, parentDocument?.documentTemplate?.name].includes(DocumentTemplateType.BidderRfIs);
  const isPayApp = docType === DocumentTemplateType.PayApplications;
  const isFinalPayApp = isPayApp && !!parentDocument.isFinal;
  const isPotentialChangeOrder = docType === DocumentTemplateType.PotentialChangeOrders;

  const isSubmittalType = [
    DocumentTemplateType.Submittals,
    DocumentTemplateType.SubmittalPackages,
    DocumentTemplateType.CloseoutSubmittals,
    DocumentTemplateType.CloseoutSubmittalPackages,
  ].some(
    (type) =>
      type === docType || type === (parentDocument?.documentTemplate?.name as DocumentTemplateType),
  );
  const documentDescription = !isSubmittalType ? parentDocument?.description?.trim() : undefined;

  // A Substitution Request carries the form the contractor filled in on the bidding portal; the
  // consultant reviewing it can pull that up without leaving this dialog. Nothing to show when the
  // bidder uploaded a completed form instead of filling it in.
  const isSubstitutionRequest = [docType, parentDocument?.documentTemplate?.name].includes(
    DocumentTemplateType.SubstitutionRequests,
  );
  const showContractorProvidedInfo =
    isSubstitutionRequest && hasSubstitutionRequestForm(parentDocument);

  useEffect(() => {
    setIsFetchingLatestFile(true);
    getLatestSubmissionFileByDocumentId(reviewDocument.id)
      .then(setLatestFile)
      .catch(() => setLatestFile(undefined))
      .finally(() => setIsFetchingLatestFile(false));
  }, [reviewDocument]);

  const stagingUpload = async () => {
    if (!uploadedFile) return;

    setLoadingText('Uploading file...');
    const response = await uploadToStaging(
      { fileName: uploadedFile.name, useMultiPartUpload: uploadedFile.size > MULTI_PART_FILE_SIZE },
      uploadedFile,
      handleUploadProgress,
      reviewDocument.id,
    );

    setLoadingText('Verifying upload...');
    const verified = await waitForFileToBeVerifiedByUrl(response.s3Key);

    if (verified) {
      setStagingFileKey(response.s3Key);
      setStagingUploadVerified(verified);
    } else {
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Something went wrong while uploading your file. Please try again.',
          severity: 'error',
        }),
      );
    }
    setUploadDialogOpen(false);
  };

  const uploadConsultantFile = async () => {
    setIsLoading(true);
    dispatch(blockNavigation());
    let verified = !uploadedFile && !bluebeamFile;
    // The markups sit in Bluebeam until the review is submitted; flattening them into a new
    // version is part of submitting, whether the review is saved in progress or completed.
    let flattenedFile: IFile | undefined;

    try {
      if (uploadedFile && stagingFileKey) {
        setLoadingText('Verifying upload...');
        await copyAdditionalReviewFromStaging(reviewDocument.id, uploadedFile.name, stagingFileKey);
        verified = stagingUploadVerified;
      } else if (bluebeamFile) {
        setLoadingText('Saving markups from Bluebeam...');
        const response = await flattenBluebeamByDocumentId(
          reviewDocument.id,
          `${bluebeamFilename}.pdf`,
        );
        flattenedFile = response.flattenedFile;
        // The version exists now, so a retry after a failed verification must not flatten again.
        clearBluebeamMarkup();
        setLoadingText('Verifying upload...');
        verified = await waitForFileToBeVerified(flattenedFile, true);
      }

      if (!verified) {
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: `File ${
              flattenedFile || uploadedFile ? (flattenedFile || uploadedFile)!.name : ''
            } has not finished uploading. You must cancel the upload and try again`,
            severity: 'error',
          }),
        );
        return;
      }

      setLoadingText('Submitting review...');
      if (isPackage) {
        await submitAdditionalReviewForPackageByDocumentId(
          reviewDocument.id,
          submittalInfo.map(({ document, actionTaken, comment }) => {
            return {
              documentId: document.id,
              recommendedActionTaken: actionTaken,
              comment,
            };
          }),
          !readyForReview,
        );
      } else {
        await submitAdditionalReviewByDocumentId(
          reviewDocument.id,
          inputActionTaken,
          inputComment,
          !readyForReview,
        );
      }
      const publicKey = getPublicDocumentKey();
      dispatch(publicKey ? fetchDocument(publicKey, true) : fetchDocument(parentDocument.id));
    } catch (e: any) {
      console.log(e);
    } finally {
      // Every exit unblocks navigation -- bailing out of a failed verification included. Leaving
      // it blocked outlives this dialog and quietly disables its close button on the next open.
      dispatch(allowNavigation());
      setIsLoading(false);
      handleClose();
    }
  };

  const handleBluebeam = async () => {
    if (latestFile) {
      await openInBluebeam(latestFile.id, latestFile.name, reviewDocument.id);
      if (refreshNotifications) refreshNotifications();
      setBluebeamFile(latestFile);
      // Keep a name the user has already edited; only seed it the first time.
      const baseName =
        latestFile.name.substring(0, latestFile.name.lastIndexOf('.')) || latestFile.name;
      setBluebeamFilename((prev) => prev || getNextBluebeamVersionName(baseName));
    }
  };

  const clearBluebeamMarkup = () => {
    setBluebeamFile(undefined);
    setBluebeamFilename('');
    setBluebeamMarkupReady(false);
  };

  const getReviewStatus = (): ConsultantReviewStatus => {
    if (!readyForReview) return 'in-progress';
    return markupsInStudio ? 'studio' : 'file';
  };

  const getSubmitDisabled = () =>
    isConsultantReviewSubmitDisabled({
      reviewStatus: getReviewStatus(),
      hasVerifiedUpload: !!uploadedFile && !!stagingFileKey,
      hasUploadSelection: !!uploadedFile,
      hasBluebeamMarkup: !!bluebeamFile,
      isBluebeamMarkupReady: bluebeamMarkupReady,
      hasComment: !!inputComment,
      hasActionTaken: !!inputActionTaken,
      isPackage,
      hasAllPackageActions: submittalInfo.every((s) => s.actionTaken),
      isRfi,
      isPayApp,
      isFinalPayApp,
    });

  const handleDownload = async (fileId: string, filename: string) => {
    try {
      setIsDownloading(true);
      await downloadFileAsConsultant(fileId, filename, reviewDocument.id);
      if (refreshNotifications) {
        await refreshNotifications();
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const closeClicked = () => {
    if (isLoading || shouldBlockNavigation) return;
    handleClose();
  };

  const handleUploadProgress = (event: any) => {
    setUploadProgress(Math.round((100 * event.loaded) / event.total));
  };

  const handleCancelUpload = async () => {
    if (!latestFile) return;
    await deleteFile(reviewDocument.id, latestFile.id);
    dispatch(reloadDocument());
  };

  const getTooltipTitle = () => {
    if (!latestFile) return '';
    if (!latestFile.isVerified) return 'Upload in progress...';
    if (fileIsPdf(latestFile)) return 'View PDF';
    return '';
  };

  const handleActionTakenChange = (event: any, index: number) => {
    const newInfo = [...submittalInfo];
    newInfo[index].actionTaken = event.target.value as ActionTakenType;
    setSubmittalInfo(newInfo);
  };

  const handleCommentChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const newInfo = [...submittalInfo];
    newInfo[index].comment = event.target.value;
    setSubmittalInfo(newInfo);
  };

  const handleRemoveComment = (commentId: string, index: number) => {
    const newInfo = [...submittalInfo];
    newInfo[index].existingComments = newInfo[index].existingComments?.filter(
      (c) => c.id !== commentId,
    );
    setSubmittalInfo(newInfo);
  };

  /**
   * Custom subscriber label if there is one, otherwise the wording this option has always used.
   * Keeping the fallback per-option preserves the context-specific strings below (e.g. "See
   * Document Comments" on a final pay application) for subscribers with no custom labels.
   */
  const labelFor = (action: ActionTakenType, fallback: string) =>
    getCustomActionTakenLabel(action, actionTakenLabels, docType) ?? fallback;

  /** Options the subscriber has switched off are not offered. */
  const renderActionTakenOptions = (options: { action: ActionTakenType; fallback: string }[]) =>
    options
      .filter(({ action }) => isActionTakenEnabled(action, disabledActionTaken, docType))
      .map(({ action, fallback }) => (
        <MenuItem key={action} value={action}>
          {labelFor(action, fallback)}
        </MenuItem>
      ));

  const getActionTakenOptions = () => {
    if (isFinalPayApp) {
      return renderActionTakenOptions([
        { action: ActionTakenType.NoExceptionsTaken, fallback: 'No Exceptions Taken' },
        { action: ActionTakenType.SeeSubmittalComments, fallback: 'See Document Comments' },
        { action: ActionTakenType.ReviseAndResubmit, fallback: 'Revise & Resubmit' },
      ]);
    }

    if (isPayApp) {
      // A draft pay application redefines these two outcomes ("as Draft" / "Return for Final
      // Submission"), so they intentionally keep built-in wording rather than a custom label —
      // matching resolveActionTakenLabel on the backend. They are still subject to enable/disable.
      return [
        { action: ActionTakenType.ReviseAndResubmit, text: 'Revise & Resubmit as Draft' },
        { action: ActionTakenType.NoExceptionsTaken, text: 'Return for Final Submission' },
      ]
        .filter(({ action }) => isActionTakenEnabled(action, disabledActionTaken, docType))
        .map(({ action, text }) => (
          <MenuItem key={action} value={action}>
            {text}
          </MenuItem>
        ));
    }

    if (isPotentialChangeOrder) {
      // A PCO renames the two recommendation outcomes; the enum values behind them are unchanged.
      // Rendered through renderActionTakenOptions like every other branch so each option keys off
      // its own action -- listing them by hand previously gave two options the same React key.
      return renderActionTakenOptions([
        { action: ActionTakenType.NoExceptionsTaken, fallback: 'Recommended for Owner Review' },
        {
          action: ActionTakenType.AmendAsNoted,
          fallback: 'Recommended for Owner Review with Qualifications',
        },
        { action: ActionTakenType.ReviseAndResubmit, fallback: 'Revise & Resubmit' },
        { action: ActionTakenType.Rejected, fallback: 'Rejected' },
      ]);
    }

    if (isSubstitutionRequest) {
      // A Substitution Request is answered once during bidding, so the outcomes that send a
      // submittal back for another round (Revise & Resubmit, Submit Specified Item) do not apply,
      // and there are no submittal comments to point at.
      return renderActionTakenOptions([
        { action: ActionTakenType.AmendAsNoted, fallback: 'Amend As Noted' },
        { action: ActionTakenType.NoExceptionsTaken, fallback: 'No Exceptions Taken' },
        { action: ActionTakenType.SeeTransmittalComments, fallback: 'See Transmittal Comments' },
        { action: ActionTakenType.Rejected, fallback: 'Rejected' },
      ]);
    }

    // Deliberately no For Information Only here: a consultant upload has never offered it, unlike
    // the architect's Review and Return.
    return renderActionTakenOptions([
      { action: ActionTakenType.AmendAsNoted, fallback: 'Amend As Noted' },
      { action: ActionTakenType.NoExceptionsTaken, fallback: 'No Exceptions Taken' },
      { action: ActionTakenType.ReviseAndResubmit, fallback: 'Revise & Resubmit' },
      { action: ActionTakenType.SeeSubmittalComments, fallback: 'See Submittal Comments' },
      { action: ActionTakenType.SeeTransmittalComments, fallback: 'See Transmittal Comments' },
      { action: ActionTakenType.SubmitSpecifiedItem, fallback: 'Submit Specified Item' },
      { action: ActionTakenType.Rejected, fallback: 'Rejected' },
    ]);
  };

  return (
    <Dialog
      open={open}
      onClose={closeClicked}
      maxWidth="md"
      PaperProps={{ style: { minWidth: 720 } }}
      className={classes.root}
    >
      <DialogTitle style={{ paddingBottom: '20px' }}>
        <div className={classes.titleContainer}>
          <span className={classes.title}>{title}</span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              lineHeight: 0,
            }}
          >
            <div className={classes.subtitle}>
              {latestFile ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <b>Reviewing: </b>
                    {latestFile.name.endsWith('.pdf') ? (
                      <PDFIcon
                        fill="#7A797A"
                        style={{ marginLeft: 4, marginRight: 6, flexShrink: 0 }}
                      />
                    ) : (
                      <InsertDriveFileOutlined htmlColor="#7A797A" style={{ fontSize: 30 }} />
                    )}
                    <Tooltip arrow placement="bottom" title={latestFile.name}>
                      <Typography
                        style={{ fontWeight: 700, textOverflow: 'ellipsis', overflow: 'hidden' }}
                      >
                        {latestFile.name}
                      </Typography>
                    </Tooltip>
                  </div>

                  {!latestFile.isVerified &&
                    (latestFile.creatorUserId === user.id || user.isSiteAdmin) && (
                      <CancelButton
                        variant="outlined"
                        onClick={handleCancelUpload}
                        style={{
                          width: '50%',
                          marginTop: 8,
                          borderColor: '#ED3F26',
                          color: '#ED3F26',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Cancel Upload
                      </CancelButton>
                    )}
                </>
              ) : (
                <b>{isFetchingLatestFile ? '' : 'No File Uploaded'}</b>
              )}
            </div>
            {latestFile && fileIsPdf(latestFile) && latestFile.isVerified ? (
              <Button
                color="primary"
                onClick={() => openInNewTab(latestFile.id)}
                style={{ paddingTop: 0, paddingBottom: 0 }}
              >
                View
              </Button>
            ) : null}
          </div>
          {documentDescription ? (
            <Typography className={classes.description}>{documentDescription}</Typography>
          ) : null}
          {showContractorProvidedInfo ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setContractorInfoDialogOpen(true)}
              style={{ marginTop: 12, height: 32, width: 'fit-content' }}
            >
              View Contractor Provided Info
            </Button>
          ) : null}
        </div>
        <IconButton
          style={{ right: '26px', top: '18px', position: 'absolute' }}
          onClick={closeClicked}
          classes={{
            root: classes.rootIconButton,
          }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
        <div className={classes.contentContainer}>
          <div className={classes.columnLayout} style={{ width: '50%' }}>
            <div
              className={classes.rowLayout}
              style={{ width: '100%', justifyContent: 'space-between' }}
            >
              {!isDownloading ? (
                <DownloadButton
                  disabled={!latestFile?.isVerified}
                  onClick={() => {
                    handleDownload(latestFile!.id, latestFile!.name);
                  }}
                  style={{ marginRight: 4, width: 150 }}
                />
              ) : (
                <CircularLoader size={30} style={{ width: 150 }} />
              )}
              <Button
                variant="outlined"
                color="primary"
                style={{ height: 32, width: 150 }}
                disabled={!!bluebeamFile || (!!uploadedFile && !!stagingFileKey)}
                onClick={() => setUploadDialogOpen(true)}
                startIcon={<Publish fill="#2C69D6" />}
              >
                UPLOAD
              </Button>
            </div>
            <Typography style={{ fontWeight: 600, alignSelf: 'center', marginTop: 12 }}>
              OR
            </Typography>
            <BluebeamButton
              disabled={
                (!!uploadedFile && !!stagingFileKey) || !!bluebeamFile || !latestFile?.isVerified
              }
              onClick={() => setBluebeamMarkupDialogOpen(true)}
              style={{ width: '100%', marginTop: 12 }}
            >
              Markup in Bluebeam Revu
            </BluebeamButton>
            {parentDocument.bluebeamStudioSessionId ? (
              <>
                <Typography style={{ fontWeight: 600, alignSelf: 'center', marginTop: 12 }}>
                  OR
                </Typography>
                <BluebeamButton
                  onClick={() => setBluebeamStudioDialogOpen(true)}
                  style={{ width: '100%', marginTop: 12 }}
                >
                  Join Bluebeam Studio Session
                </BluebeamButton>
              </>
            ) : null}

            {bluebeamFile ? (
              <BluebeamFileField
                documentId={reviewDocument.id}
                filename={bluebeamFilename}
                onFilenameChange={setBluebeamFilename}
                onReadyChange={setBluebeamMarkupReady}
                onRemove={clearBluebeamMarkup}
              />
            ) : null}

            <div
              className={classes.file}
              style={{ visibility: uploadedFile && stagingFileKey ? 'visible' : 'hidden' }}
            >
              <IconButton
                onClick={() => {
                  setUploadedFile(undefined);
                  setStagingFileKey(undefined);
                  setStagingUploadVerified(false);
                }}
                style={{ padding: 0, marginRight: 8, marginLeft: -2 }}
              >
                <HighlightOffRounded />
              </IconButton>
              {uploadedFile?.name.endsWith('.pdf') ? (
                <PDFIcon />
              ) : (
                <InsertDriveFileOutlined htmlColor="#7A797A" style={{ fontSize: 30 }} />
              )}
              <Typography className={classes.fileText}>{(uploadedFile as File)?.name}</Typography>
            </div>
            <FormControl style={{ marginTop: 16 }}>
              <RadioGroup
                value={getReviewStatus()}
                onChange={(event, value) => {
                  setReadyForReview(value !== 'in-progress');
                  setMarkupsInStudio(value === 'studio');
                }}
              >
                <FormControlLabel
                  value="in-progress"
                  control={<Radio style={{ padding: '0px 9px' }} color="primary" />}
                  label="Review in progress"
                />
                <FormControlLabel
                  value="file"
                  control={<Radio color="primary" />}
                  label="Review complete, file uploaded"
                />
                {parentDocument.bluebeamStudioSessionId ? (
                  <FormControlLabel
                    value="studio"
                    control={<Radio color="primary" />}
                    label="Review complete, markups in Bluebeam Studio"
                  />
                ) : null}
              </RadioGroup>
            </FormControl>
            {!isRfi && !isPackage ? (
              <FormControl fullWidth variant="outlined" style={{ marginTop: 12, width: '100%' }}>
                <Typography variant="body1" style={{ paddingBottom: 4 }}>
                  Recommended action
                </Typography>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  disabled={!readyForReview}
                  value={inputActionTaken}
                  onChange={(e: any) => {
                    setInputActionTaken(e.target.value);
                  }}
                  MenuProps={{
                    // @ts-ignore
                    getContentAnchorEl: () => null,
                    autoFocus: true,
                  }}
                  fullWidth
                >
                  {getActionTakenOptions()}
                </Select>
              </FormControl>
            ) : null}
          </div>
          <div className={classes.columnLayout} style={{ width: 320 }}>
            <Typography style={{ fontSize: 14, fontWeight: 600 }}>TIP:</Typography>
            <div
              className={classes.columnLayout}
              style={{ borderWidth: 1, borderStyle: 'solid', borderColor: '#000', padding: 8 }}
            >
              <Typography style={{ fontSize: 13 }}>
                {docType === DocumentTemplateType.DesignPackages
                  ? designPackageTipHeading
                  : defaultTipHeading}
              </Typography>
              <Typography style={{ fontSize: 13, marginTop: 16 }}>
                {docType === DocumentTemplateType.DesignPackages
                  ? designPackageTipBody
                  : defaultTipBody}
              </Typography>
            </div>

            {!isPackage ? (
              <TextField
                variant="outlined"
                multiline
                rows={!isRfi ? 9 : 4}
                value={inputComment}
                onChange={(event) => setInputComment(event.target.value)}
                label="Comments..."
                InputLabelProps={{ style: { textTransform: 'capitalize' } }}
                InputProps={{ style: { padding: '10px' } }}
                className={classes.textfield}
                style={{ marginTop: 20 }}
              />
            ) : null}
          </div>
        </div>
        {isPackage && readyForReview ? (
          <PackageListTable
            submittalInfo={submittalInfo}
            handleActionTakenChange={handleActionTakenChange}
            handleCommentChange={handleCommentChange}
            handleRemoveComment={handleRemoveComment}
            isConsultantReview
          />
        ) : null}
      </DialogContent>

      <DialogActions className={classes.actions}>
        {isLoading ? (
          <>
            <Typography style={{ fontWeight: 500 }}>{loadingText}</Typography>
            {loadingText.includes('Uploading') ? (
              <>
                <Typography style={{ fontSize: 14, marginTop: 4 }}>{uploadProgress}%</Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  style={{ height: 8, width: '100%', marginTop: 4, marginBottom: 8 }}
                />
              </>
            ) : (
              <CircularLoader style={{ marginTop: 4, marginBottom: 4 }} />
            )}
            <Typography style={{ fontWeight: 500 }}>DO NOT NAVIGATE AWAY FROM THIS PAGE</Typography>
          </>
        ) : (
          <Button
            variant="contained"
            color="primary"
            disabled={getSubmitDisabled()}
            onClick={uploadConsultantFile}
            startIcon={<Publish style={{ marginBottom: 3 }} />}
            style={{ width: '100%' }}
          >
            {readyForReview ? 'Return to architect' : 'Save (In Progress Selected)'}
          </Button>
        )}
      </DialogActions>
      {bluebeamMarkupDialogOpen ? (
        <BluebeamFlattenDialog
          dialogOpen={bluebeamMarkupDialogOpen}
          handleClose={() => setBluebeamMarkupDialogOpen(false)}
          handleMarkup={handleBluebeam}
          documentId={reviewDocument.id}
          file={latestFile}
          isConsultant
          disableMarkup={!latestFile?.isVerified}
          inlineNaming
        />
      ) : null}
      {bluebeamStudioDialogOpen ? (
        <BluebeamFlattenDialog
          dialogOpen={bluebeamStudioDialogOpen}
          handleClose={() => setBluebeamStudioDialogOpen(false)}
          documentId={reviewDocument.id}
          isConsultant
          disableMarkup
          studioSessionId={parentDocument.bluebeamStudioSessionId}
        />
      ) : null}
      {showContractorProvidedInfo && parentDocument ? (
        <SubstitutionRequestInfoDialog
          open={contractorInfoDialogOpen}
          handleClose={() => setContractorInfoDialogOpen(false)}
          document={parentDocument}
        />
      ) : null}
      <FileUploadDialog
        open={uploadDialogOpen}
        handleClose={() => setUploadDialogOpen(false)}
        title="Consultant Upload"
        addFile={(f) => setUploadedFile(f as File)}
        removeFile={() => {
          setUploadedFile(undefined);
          setStagingFileKey(undefined);
          setStagingUploadVerified(false);
        }}
        handleSubmit={stagingUpload}
        uploadProgress={uploadProgress}
        uploadMessage={loadingText}
        file={uploadedFile}
        canSubmit={!!uploadedFile}
        disableDesignUpload
        disableComments
        buttonType="submit"
      />
    </Dialog>
  );
}
